// Visionneuse de documents (BAT, maquette, photo) : plein écran, page par page, zoom au doigt.
// Utilisée sur la tablette du poste (sombre, gros boutons) et dans la fiche du job.
import { useCallback, useEffect, useRef, useState, type PointerEvent as RPointerEvent } from "react";
import { DOC_KIND_LABEL, type JobDocument } from "../data/types.ts";

export function DocViewer({ docs, loadPage, startDoc, onClose, tablet = false }: {
  docs: JobDocument[];
  loadPage: (docId: string, page: number) => Promise<string>;
  startDoc?: string;
  onClose: () => void;
  tablet?: boolean;
}) {
  const [docId, setDocId] = useState(startDoc || docs[0]?.id);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const cache = useRef(new Map<string, string>());
  const doc = docs.find((d) => d.id === docId) || docs[0];
  const total = doc?.pages || 1;

  const fetchPage = useCallback(async (id: string, p: number) => {
    const k = `${id}:${p}`;
    if (cache.current.has(k)) return cache.current.get(k)!;
    const data = await loadPage(id, p);
    cache.current.set(k, data);
    return data;
  }, [loadPage]);

  useEffect(() => {
    if (!doc) return;
    let alive = true;
    setErr(""); setSrc(cache.current.get(`${doc.id}:${page}`) || null);
    fetchPage(doc.id, page).then((d) => { if (alive) setSrc(d); }).catch(() => { if (alive) setErr("Cette page n'a pas pu être chargée. Vérifiez la connexion."); });
    // page suivante préparée à l'avance
    if (page < total) fetchPage(doc.id, page + 1).catch(() => {});
    return () => { alive = false; };
  }, [doc, page, total, fetchPage]);

  const go = useCallback((d: number) => { setZoom(false); setPage((p) => Math.min(total, Math.max(1, p + d))); }, [total]);
  useEffect(() => {
    const f = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", f);
    return () => window.removeEventListener("keydown", f);
  }, [go, onClose]);

  // glisser le doigt à gauche ou à droite pour changer de page (hors zoom)
  const swipe = useRef<{ x: number; y: number } | null>(null);
  const down = (e: RPointerEvent) => { if (!zoom) swipe.current = { x: e.clientX, y: e.clientY }; };
  const up = (e: RPointerEvent) => {
    const s = swipe.current; swipe.current = null;
    if (!s || zoom) return;
    const dx = e.clientX - s.x, dy = e.clientY - s.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) go(dx < 0 ? 1 : -1);
    else if (Math.abs(dx) < 8 && Math.abs(dy) < 8) setZoom(true);
  };

  if (!doc) return null;
  return (
    <div className={`docv ${tablet ? "docv-tablet" : ""}`} role="dialog" aria-modal="true" aria-label={doc.name || DOC_KIND_LABEL[doc.kind]}>
      <div className="docv-bar">
        <div className="docv-tabs">
          {docs.map((d) => (
            <button key={d.id} className={d.id === doc.id ? "on" : ""} onClick={() => { setDocId(d.id); setPage(1); setZoom(false); }}>
              <b>{DOC_KIND_LABEL[d.kind]}</b><span>{d.name}</span>
            </button>
          ))}
        </div>
        <button className="docv-close" onClick={onClose}>Fermer</button>
      </div>
      <div className={`docv-stage ${zoom ? "zoomed" : ""}`} onPointerDown={down} onPointerUp={up}>
        {err ? <p className="docv-msg">{err}</p> : src ? (
          <img src={src} alt={`${doc.name}, page ${page} sur ${total}`} draggable={false} onClick={zoom ? () => setZoom(false) : undefined} />
        ) : <p className="docv-msg">Chargement de la page…</p>}
      </div>
      <div className="docv-foot">
        <button className="docv-nav" onClick={() => go(-1)} disabled={page <= 1}>‹ Page précédente</button>
        <span className="docv-count">Page {page} / {total}</span>
        <button className="docv-zoom" onClick={() => setZoom(!zoom)}>{zoom ? "Vue entière" : "Zoomer"}</button>
        <button className="docv-nav" onClick={() => go(1)} disabled={page >= total}>Page suivante ›</button>
      </div>
    </div>
  );
}
